import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";

import {
  NON_TERMINAL_STATUSES,
  QUOTE_CRITICAL_MS,
  QUOTE_SUBMIT_GUARD_MS,
  QUOTE_WARN_MAX_MS,
  STATUS_BUCKET,
  type CryptoSwapBucket,
} from "../constants/cryptoSwaps.constants";
import type { OtcOrderStatus, OtcQuote } from "../types/cryptoSwaps.types";
import type { AmountCheck } from "./cryptoSwapAmounts";

export function bucketForStatus(status: OtcOrderStatus): CryptoSwapBucket {
  return STATUS_BUCKET[status] ?? "unknown";
}

/** Drives the polling and the pulsing dot. */
export function isNonTerminalStatus(status: OtcOrderStatus): boolean {
  return NON_TERMINAL_STATUSES.includes(status);
}

/** Every reason the form refuses to open the review, most specific first. */
export type CryptoSwapBlock =
  | null
  | "readonly"
  | "organization"
  | "unavailable"
  | "empty"
  | "amount"
  | "pricing"
  | "unpriced"
  | "stale"
  | "expired"
  | "balance"
  | "destination";

/** The review adds one requirement of its own: the irreversibility acknowledgement. */
export type CryptoSwapSubmitBlock = CryptoSwapBlock | "confirmation";

export interface CryptoSwapBlockInput {
  canWrite: boolean;
  /** Staff must pick the organization whose wallet is debited. */
  organizationReady: boolean;
  pairAvailable: boolean;
  amountCheck: AmountCheck;
  quote: Pick<OtcQuote, "expires_at"> | null;
  isPricing: boolean;
  /** The last pricing attempt refused the amount against the limits, rather than failing to answer. */
  amountRejected: boolean;
  /** Identity of the inputs the quote was priced for, and of what the form holds now. */
  quoteSignature: string | null;
  formSignature: string;
  /** Injected so the button and the handler read the same clock. */
  now: number;
  /** The organization has no wallet in the send currency, so no amount can be bought. */
  walletMissing: boolean;
  /** The quote debits more than the wallet's available balance. */
  exceedsBalance: boolean;
  destinationValid: boolean;
}

/** The single predicate behind BOTH the review button's `disabled` and the submit handler's guard. */
export function resolveCryptoSwapBlock(input: CryptoSwapBlockInput): CryptoSwapBlock {
  if (!input.canWrite) return "readonly";
  if (!input.organizationReady) return "organization";
  if (!input.pairAvailable) return "unavailable";
  if (input.walletMissing) return "balance";
  if (input.amountCheck === "empty") return "empty";
  if (input.amountCheck !== "ok") return "amount";
  if (input.isPricing) return "pricing";
  if (!input.quote) return input.amountRejected ? "amount" : "unpriced";
  if (input.quoteSignature !== input.formSignature) return "stale";

  const deadline = quoteDeadlineMs(input.quote);
  if (deadline !== null && deadline <= input.now) return "expired";

  if (input.exceedsBalance) return "balance";
  if (!input.destinationValid) return "destination";
  return null;
}

export function resolveSubmitBlock(block: CryptoSwapBlock, acknowledged: boolean): CryptoSwapSubmitBlock {
  return block ?? (acknowledged ? null : "confirmation");
}

export const CRYPTO_SWAP_BLOCK_MESSAGE_KEYS: Record<
  NonNullable<CryptoSwapBlock>,
  keyof CryptoSwapsTranslations
> = {
  readonly: "block_readonly",
  organization: "block_organization",
  unavailable: "block_unavailable",
  empty: "block_empty",
  amount: "block_amount",
  pricing: "block_pricing",
  unpriced: "block_unpriced",
  stale: "block_pricing",
  expired: "block_expired",
  balance: "block_balance",
  destination: "block_destination",
};

/** The countdown and the submit guard share one deadline: the expiry minus the guard. */
export function quoteDeadlineMs(quote: Pick<OtcQuote, "expires_at"> | null | undefined): number | null {
  if (!quote) return null;
  const expires = Date.parse(quote.expires_at);
  return Number.isFinite(expires) ? expires - QUOTE_SUBMIT_GUARD_MS : null;
}

/** How long the quote could be acted on, from its own two timestamps. Null when either is unreadable. */
export function quoteUsableLifeMs(
  quote: Pick<OtcQuote, "created_at" | "expires_at"> | null | undefined,
): number | null {
  if (!quote) return null;
  const created = Date.parse(quote.created_at);
  const deadline = quoteDeadlineMs(quote);
  return Number.isFinite(created) && deadline !== null && deadline > created ? deadline - created : null;
}

export type QuoteStage = "calm" | "warn" | "critical" | "expired";

/** Scaled to the quote's own life so a long TTL does not spend two-thirds of it amber. */
export function quoteStage(remainingMs: number, lifeMs: number | null | undefined): QuoteStage {
  if (remainingMs <= 0) return "expired";
  if (remainingMs <= QUOTE_CRITICAL_MS) return "critical";
  const life = lifeMs && lifeMs > 0 ? lifeMs : QUOTE_WARN_MAX_MS * 3;
  return remainingMs <= Math.min(QUOTE_WARN_MAX_MS, life / 3) ? "warn" : "calm";
}
